import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, TrendingDown, Building, Clock, Scale, Shield } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { LegalLanguageBanner } from '../components/LegalLanguageBanner';

const RiskCard = ({ icon: Icon, title, children }) => (
  <div className="bg-[#1a1a1a] border border-[#C4A55A]/20 rounded-2xl p-6 md:p-7">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
        <Icon className="h-5 w-5 text-amber-300" />
      </div>
      <h3 className="text-lg font-semibold text-white">{title}</h3>
    </div>
    <div className="text-gray-300 leading-relaxed text-sm">{children}</div>
  </div>
);

export const Risikohinweise = () => {
  return (
    <div className="bg-black min-h-screen text-white">
      <Header />
      <main className="container mx-auto px-4 pt-32 pb-16 max-w-4xl" data-testid="page-risikohinweise">
        <Link
          to="/"
          className="inline-flex items-center text-[#C4A55A] hover:text-[#D4B56A] mb-6 text-sm"
          data-testid="risiko-back-link"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück zur Startseite
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <AlertTriangle className="h-10 w-10 text-amber-400" />
          <h1 className="text-4xl md:text-5xl font-bold">Risikohinweise</h1>
        </div>
        <p className="text-gray-400 mb-8">
          Bitte lesen Sie diese Risikohinweise sorgfältig, bevor Sie Aktien der Löwenherz Invest AG
          zeichnen. Ein Investment ist immer mit Chancen und Risiken verbunden.
        </p>

        <LegalLanguageBanner />

        {/* Warning banner */}
        <div className="bg-amber-500/10 border border-amber-500/40 rounded-2xl p-5 mb-10">
          <p className="text-amber-200 text-sm leading-relaxed">
            <strong>Wichtig:</strong> Aktien­investitionen können zu einem teilweisen oder vollständigen
            Verlust des eingesetzten Kapitals führen. Investieren Sie nur Beträge, deren Verlust Sie
            wirtschaftlich verkraften können. Diese Risiko­hinweise ersetzen keine individuelle
            Anlage­beratung.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
          <RiskCard icon={TrendingDown} title="Totalverlustrisiko">
            Wert­papiere können vollständig an Wert verlieren. Im Falle einer Insolvenz der
            Gesellschaft kann das eingesetzte Kapital ganz oder teilweise verloren gehen.
          </RiskCard>

          <RiskCard icon={Building} title="Branchenrisiko Immobilien">
            Die Investitionen erfolgen in Schweizer Immobilien. Der Immobilien­markt unterliegt
            zyklischen Schwankungen, Zinsänderungen, regulatorischen Eingriffen und lokalen
            Markt­besonderheiten.
          </RiskCard>

          <RiskCard icon={Clock} title="Illiquidität">
            Die Aktien sind nicht börsen­kotiert. Ein kurz­fristiger Verkauf ist daher nicht
            jederzeit möglich. Investoren sollten einen Anlage­horizont von <strong>mindestens
            5–10 Jahren</strong> einplanen.
          </RiskCard>

          <RiskCard icon={Scale} title="Keine Renditegarantie">
            Vergangene Wert­entwicklungen oder Projekt­erfolge sind keine Garantie für zukünftige
            Erträge. Dividenden­zahlungen unterliegen dem Beschluss der General­versammlung und
            sind nicht garantiert.
          </RiskCard>

          <RiskCard icon={Shield} title="Konzentrations­risiko">
            Eine zu hohe Gewichtung eines einzelnen Investments im Gesamt­portfolio erhöht das
            Risiko. Wir empfehlen eine breite Diversifikation Ihres Vermögens.
          </RiskCard>

          <RiskCard icon={AlertTriangle} title="Steuerliche Risiken">
            Die steuerliche Behandlung kann sich ändern und ist abhängig von Ihrer persönlichen
            Situation. Konsultieren Sie bitte einen Steuer­berater in Ihrem Wohnsitz­kanton bzw.
            -land.
          </RiskCard>
        </div>

        <section className="bg-[#1a1a1a] border border-[#C4A55A]/20 rounded-2xl p-6 md:p-8 mb-6">
          <h2 className="text-2xl font-semibold mb-4">Keine Anlage- oder Steuerberatung</h2>
          <p className="text-gray-300 leading-relaxed">
            Die auf dieser Website bereitgestellten Informationen dienen ausschliesslich
            Informations­zwecken und stellen weder eine individuelle Anlage­beratung noch eine
            Steuer­beratung oder Empfehlung zum Kauf oder Verkauf von Wert­papieren dar. Für
            individuelle Beratung wenden Sie sich bitte an Ihren persönlichen Berater.
          </p>
        </section>

        <section className="bg-[#1a1a1a] border border-[#C4A55A]/20 rounded-2xl p-6 md:p-8 mb-6">
          <h2 className="text-2xl font-semibold mb-4">Empfohlene Vorgehensweise</h2>
          <ul className="list-disc pl-6 space-y-2 text-gray-300">
            <li>Lesen Sie alle bereitgestellten Unterlagen (AGB, Datenschutz, Geschäftsbericht) sorgfältig.</li>
            <li>Investieren Sie nur Mittel, deren Verlust Sie verkraften können.</li>
            <li>Diversifizieren Sie Ihr Portfolio – nicht alles in ein einzelnes Investment.</li>
            <li>Planen Sie einen langfristigen Anlage­horizont (5–10 Jahre).</li>
            <li>Holen Sie individuelle Beratung bei einem unabhängigen Vermögens- und Steuerberater ein.</li>
          </ul>
        </section>

        <div className="bg-[#C4A55A]/10 border border-[#C4A55A]/30 rounded-2xl p-6 md:p-8">
          <h3 className="text-xl font-semibold mb-3">Fragen zum Risiko?</h3>
          <p className="text-gray-300">
            Wir stehen Ihnen für Rückfragen jederzeit zur Verfügung. Schreiben Sie uns an{' '}
            <a href="mailto:office@loewenherz.ag" className="text-[#C4A55A] hover:text-[#D4B56A] font-medium">
              office@loewenherz.ag
            </a>{' '}
            oder rufen Sie an unter +41 52 55 101 40.
          </p>
        </div>

        <p className="text-gray-500 text-sm mt-10 text-center">
          Stand: {new Date().toLocaleDateString('de-CH', { day: '2-digit', month: 'long', year: 'numeric' })}
        </p>
      </main>
      <Footer />
    </div>
  );
};
